import { appendSearchParams } from '@alexis/helpers/searchParam';
import axios from 'axios';
import type { CancelToken } from 'axios';

import { User } from '@wego/alexis/types/helpers/user';

import type { HotelBookingRoomTypes } from '@wegoTypes/hotelBooking/hotelRoomTypes';
import type { PriceCheck } from '@wegoTypes/hotelBooking/priceCheck';

export async function getPriceCheck(
  cancelToken: CancelToken,
  countryCode: string,
  currencyCode: string,
  hotelId: number,
  locale: string,
  metasearchHotelRateParamsRateId: string,
  metasearchHotelRateParamsSessionId: string,
  rateId: string,
  searchId: string,
  wegoAnalyticsClientId: string,
  wegoAnalyticsClientSessionId: string,
): Promise<PriceCheck> {
  const url = `${API_BASE_URL}/yorktown/v2/price`;

  const body = {
    currency: currencyCode,
    rate_id: metasearchHotelRateParamsRateId,
    reference_id: rateId,
    search_id: searchId,
    site_code: countryCode,
    wego_hotel_id: hotelId.toString(),
    wego_rate_id: rateId,
  };

  const response = await axios.post<PriceCheck>(url, body, {
    headers: {
      'X-Hib-Session-Id': metasearchHotelRateParamsSessionId,
      'Wego-Analytics-Client-Id': wegoAnalyticsClientId,
      'Wego-Analytics-Client-Session-Id': wegoAnalyticsClientSessionId,
      'Accept-Language': locale,
    },
    cancelToken,
    withCredentials: true,
  });

  if (response.status === 200) {
    return response.data;
  }
  throw new Error(response.statusText);
}

export async function getPaymentOption(
  apiBaseUrl: string,
  metasearchHotelRateParamsSessionId: string,
  locale: string,
  cancelToken: CancelToken,
  priceCheckPrebookId: string,
): Promise<HotelPaymentOption> {
  const url = appendSearchParams(`${apiBaseUrl}/yorktown/v2/payment/options`, {
    prebook_id: priceCheckPrebookId,
  });

  const response = await axios.get<HotelPaymentOption>(url, {
    headers: { session_id: metasearchHotelRateParamsSessionId, 'Accept-Language': locale },
    cancelToken,
    withCredentials: true,
  });

  if (response.status === 200) {
    return response.data;
  }
  throw new Error(response.statusText);
}

export async function createBooking(
  apiBaseUrl: string,
  hotelBookingRequest: HotelBookingRequest,
  metasearchHotelRateParamsSessionId: string,
  locale: string,
  cancelToken?: CancelToken,
): Promise<HotelCreateBooking> {
  const url = `${apiBaseUrl}/yorktown/v2/booking`;

  const response = await axios.post<HotelCreateBooking>(url, hotelBookingRequest, {
    headers: { session_id: metasearchHotelRateParamsSessionId, 'Accept-Language': locale },
    cancelToken,
    withCredentials: true,
  });

  if (response.status === 200) {
    return response.data;
  }
  throw new Error(response.statusText);
}

export async function getBooking(
  encryptedBookingId: string,
  locale: string,
  cancelToken: CancelToken,
): Promise<HotelBookingResult> {
  const url = `${API_BASE_URL}/yorktown/booking/${encodeURIComponent(encryptedBookingId)}`;

  const response = await axios.get<HotelBookingResult>(url, {
    headers: { 'Accept-Language': locale },
    cancelToken,
    withCredentials: true,
  });

  if (response.status === 200) {
    return response.data;
  }
  throw new Error(response.statusText);
}

export async function reservePromoCode(
  apiBaseUrl: string,
  wegoAnalyticsClientId: string,
  locale: string,
  metasearchHotelRateParamsSessionId: string,
  prebookId: string,
  promoCode: string,
): Promise<HotelBookingPromoCodeResponse> {
  // we let the user search with empty promo code, as we need to show the error message by locale from the API itself
  const url = `${apiBaseUrl}/yorktown/promo-codes/${promoCode || 'NO_PROMO_CODE'}/reserve`;

  const body = {
    client_id: wegoAnalyticsClientId,
    prebook_id: prebookId,
  };

  const response = await axios.post<HotelBookingPromoCodeResponse>(url, body, {
    headers: {
      session_id: metasearchHotelRateParamsSessionId,
      'Accept-Language': locale,
    },
    withCredentials: true,
  });

  if (response.status === 200) {
    return response.data;
  }
  throw new Error(response.statusText);
}

export async function releasePromoCode(
  apiBaseUrl: string,
  locale: string,
  metasearchHotelRateParamsSessionId: string,
  prebookId: string,
  promoCode: string,
): Promise<HotelBookingPromoCodeResponse> {
  const url = `${apiBaseUrl}/yorktown/promo-codes/${promoCode}/release`;

  const body = {
    prebook_id: prebookId,
  };

  const response = await axios.post<HotelBookingPromoCodeResponse>(url, body, {
    headers: { session_id: metasearchHotelRateParamsSessionId, 'Accept-Language': locale },
    withCredentials: true,
  });

  if (response.status === 200) {
    return response.data;
  }
  throw new Error(response.statusText);
}

export async function getHotelPaymentStatus(
  encryptedBookingId: string,
  locale: string,
  cancelToken?: CancelToken,
): Promise<HotelPaymentStatus> {
  const url = `${API_BASE_URL}/yorktown/v2/payment/${encryptedBookingId}`;

  const response = await axios.get<HotelPaymentStatus>(url, {
    headers: {
      'Accept-Language': locale,
    },
    cancelToken,
    withCredentials: true,
  });

  if (response.status === 200) {
    return response.data;
  }
  throw new Error(response.statusText);
}

export async function getHotelRoomTypes(
  apiBaseUrl: string,
  cancelToken: CancelToken,
  hotelId: number,
  locale: string,
  metasearchHotelRateParamsSessionId: string,
  searchId: string,
  wegoAnalyticsClientId: string,
  wegoAnalyticsClientSessionId: string,
): Promise<HotelBookingRoomTypes> {
  const url = `${apiBaseUrl}/yorktown/hotels/${hotelId}/room_types?search_id=${searchId}`;

  const response = await axios.get<HotelBookingRoomTypes>(url, {
    headers: {
      'X-Hib-Session-Id': metasearchHotelRateParamsSessionId,
      'Wego-Analytics-Client-Id': wegoAnalyticsClientId,
      'Wego-Analytics-Client-Session-Id': wegoAnalyticsClientSessionId,
      'Accept-Language': locale,
    },
    cancelToken,
    withCredentials: true,
  });

  if (response.status === 200) {
    return response.data;
  }
  throw new Error(response.statusText);
}

export async function cancelHotelBooking(
  bookingId: string,
  domainBasedApiBaseUrl: string,
  lastName: string,
  locale: string,
  notes: string,
  user: User,
): Promise<void> {
  const url = `${domainBasedApiBaseUrl}/yorktown/bookings/${bookingId}/cancel`;

  const data = {
    booking_note: {
      author: user.email,
      notes,
    },
    last_name: lastName,
  };

  const response = await axios.patch(url, data, {
    headers: { 'Accept-Language': locale },
    withCredentials: true,
  });

  if (response.status !== 200) {
    throw new Error(response.statusText);
  }
}
